"use client";

import CurrencyIcon from "@/components/icons/CurrencyIcon";
import EsimIcon from "../../../../../public/icon/home-page/esim";
import FlightsIcon from "../../../../../public/icon/home-page/flights";
import HotelsIcon from "../../../../../public/icon/home-page/hotels";
import ToursIcon from "../../../../../public/icon/home-page/tours";
import LightningIcon from "@/components/icons/LightningIcon";
import SupportIcon from "@/components/icons/SupportIcon";
import ShieldIcon from "@/components/icons/ShieldIcon";
import GlobeIcon from "@/components/icons/GlobeIcon";
import StarIcon from "@/components/icons/StartIcon";
import ClockIcon from "@/components/icons/ClockIcon";
import UsersIcon from "@/components/icons/UsersIcon";
import MapIcon from "@/components/icons/MapIcon";
import SearchingFlights from "../../../flight/FormSearch/desktop/SearchingFlights";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import SearchingHotels from "../../../hotel/FormSearch/desktop/SearchingHotels";
import FormSearchTours from "../../../tours/FormSearch";
import FormSearchEsim from "../../../esim/FormSearch";
import FormSearchVisa from "../../../visa/FormSearch";
import VisaHeroHighlights from "../../../../features/visa/components/VisaHeroHighlights";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card, Col, Form, Row } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPassport } from "@fortawesome/free-solid-svg-icons";

type SearchTabKey = "flights" | "hotels" | "tours" | "esim" | "visa";

type TaglineFeature = {
  key: string;
  icon: ReactNode;
  titleKey: string;
  descKey: string;
};

type TaglineContent = {
  variant: "flights" | "hotels" | "tours" | "esim" | "visa";
  icon: ReactNode;
  titleKey: string;
  descKey: string;
  features: TaglineFeature[];
};

const SearchingVendorDesktop = () => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const [tab, setTab] = useState<SearchTabKey>("flights");

  if (!isDesktop) {
    return <></>;
  }

  const tabs: { key: SearchTabKey; label: string; icon?: ReactNode }[] = [
    { key: "flights", label: t("common.flight") },
    { key: "hotels", label: t("common.hotel") },
    { key: "tours", label: t("common.tour") },
    { key: "esim", label: t("common.esim") },
    { key: "visa", label: "Visa" },
  ];

  const renderTabIcon = (key: SearchTabKey, isActive: boolean) => {
    const activeColor = isActive ? "#4382E3" : undefined;

    if (key === "flights") {
      return <FlightsIcon color={activeColor} />;
    }
    if (key === "hotels") {
      return <HotelsIcon color={activeColor} />;
    }
    if (key === "tours") {
      return <ToursIcon color={activeColor} />;
    }
    if (key === "esim") {
      return <EsimIcon color={activeColor} style={{ fontSize: "2.3rem" }} />;
    }
    if (key === "visa") {
      return (
        <FontAwesomeIcon
          icon={faPassport}
          color={activeColor}
          style={{ fontSize: "2rem" }}
        />
      );
    }

    return tabs.find((tab) => tab.key === key)?.icon;
  };

  const taglineByTab: Partial<Record<SearchTabKey, TaglineContent>> = {
    flights: {
      variant: "flights",
      icon: <FlightsIcon color="#2F6FDD" />,
      titleKey: "home.tagline_flights_title",
      descKey: "home.tagline_flights_desc",
      features: [
        {
          key: "price",
          icon: <CurrencyIcon aria-hidden />,
          titleKey: "home.tagline_flights_feat_price_title",
          descKey: "home.tagline_flights_feat_price_desc",
        },
        {
          key: "fast",
          icon: <LightningIcon aria-hidden />,
          titleKey: "home.tagline_flights_feat_fast_title",
          descKey: "home.tagline_flights_feat_fast_desc",
        },
        {
          key: "support",
          icon: <SupportIcon aria-hidden />,
          titleKey: "home.tagline_flights_feat_support_title",
          descKey: "home.tagline_flights_feat_support_desc",
        },
      ],
    },
    hotels: {
      variant: "hotels",
      icon: <HotelsIcon color="#1E9E6A" />,
      titleKey: "home.tagline_hotels_title",
      descKey: "home.tagline_hotels_desc",
      features: [
        {
          key: "price",
          icon: <CurrencyIcon aria-hidden />,
          titleKey: "home.tagline_hotels_feat_price_title",
          descKey: "home.tagline_hotels_feat_price_desc",
        },
        {
          key: "freecancel",
          icon: <ShieldIcon aria-hidden />,
          titleKey: "home.tagline_hotels_feat_freecancel_title",
          descKey: "home.tagline_hotels_feat_freecancel_desc",
        },
        {
          key: "variety",
          icon: <GlobeIcon aria-hidden />,
          titleKey: "home.tagline_hotels_feat_variety_title",
          descKey: "home.tagline_hotels_feat_variety_desc",
        },
        {
          key: "review",
          icon: <StarIcon aria-hidden />,
          titleKey: "home.tagline_hotels_feat_review_title",
          descKey: "home.tagline_hotels_feat_review_desc",
        },
      ],
    },
    tours: {
      variant: "tours",
      icon: <ToursIcon color="#0E9F6E" />,
      titleKey: "home.tagline_tours_title",
      descKey: "home.tagline_tours_desc",
      features: [
        {
          key: "departure",
          icon: <ClockIcon aria-hidden />,
          titleKey: "home.tagline_tours_feat_departure_title",
          descKey: "home.tagline_tours_feat_departure_desc",
        },
        {
          key: "guide",
          icon: <UsersIcon aria-hidden />,
          titleKey: "home.tagline_tours_feat_guide_title",
          descKey: "home.tagline_tours_feat_guide_desc",
        },
        {
          key: "price",
          icon: <CurrencyIcon aria-hidden />,
          titleKey: "home.tagline_tours_feat_price_title",
          descKey: "home.tagline_tours_feat_price_desc",
        },
        {
          key: "itinerary",
          icon: <MapIcon aria-hidden />,
          titleKey: "home.tagline_tours_feat_itinerary_title",
          descKey: "home.tagline_tours_feat_itinerary_desc",
        },
      ],
    },
    esim: {
      variant: "esim",
      icon: <EsimIcon color="#4F46E5" />,
      // Dùng chung nội dung với trang /esim-du-lich (esim.hub_title/hub_subtitle) — tránh 2 bộ copy lệch nhau.
      titleKey: "esim.hub_title",
      descKey: "esim.hub_subtitle",
      features: [
        {
          key: "multi_country",
          icon: <GlobeIcon aria-hidden />,
          titleKey: "esim.hub_highlight_title_multi_country",
          descKey: "esim.hub_highlight_desc_multi_country",
        },
        {
          key: "network",
          icon: <StarIcon aria-hidden />,
          titleKey: "esim.hub_highlight_title_network",
          descKey: "esim.hub_highlight_desc_network",
        },
        {
          key: "support",
          icon: <SupportIcon aria-hidden />,
          titleKey: "esim.hub_highlight_title_support",
          descKey: "esim.hub_highlight_desc_support",
        },
        {
          key: "easy",
          icon: <LightningIcon aria-hidden />,
          titleKey: "esim.hub_highlight_title_easy",
          descKey: "esim.hub_highlight_desc_easy",
        },
      ],
    },
    visa: {
      variant: "visa",
      icon: <FontAwesomeIcon icon={faPassport} color="#B45309" />,
      titleKey: "home.tagline_visa_title",
      descKey: "home.tagline_visa_desc",
      features: [
        {
          key: "rate",
          icon: <ShieldIcon aria-hidden />,
          titleKey: "home.tagline_visa_feat_rate_title",
          descKey: "home.tagline_visa_feat_rate_desc",
        },
        {
          key: "fast",
          icon: <LightningIcon aria-hidden />,
          titleKey: "home.tagline_visa_feat_fast_title",
          descKey: "home.tagline_visa_feat_fast_desc",
        },
        {
          key: "support",
          icon: <SupportIcon aria-hidden />,
          titleKey: "home.tagline_visa_feat_support_title",
          descKey: "home.tagline_visa_feat_support_desc",
        },
      ],
    },
  };

  const activeTagline = taglineByTab[tab];

  return (
    <Form style={{ zIndex: 1 }}>
      <Row justify="center" className="home__searching">
        <Col span={24}>
          <div className="home__searching-hero-strip">
            <div className="home__searching-hero-copy">
              <div className="home__searching-hero-swoosh" aria-hidden="true">
                <svg viewBox="0 0 480 100" preserveAspectRatio="xMinYMax meet">
                  {/* vệt lượn chữ S, võng giữa rồi hướng lên mũi tên */}
                  <path
                    className="swoosh-trail"
                    d="M5 88 C60 84 105 58 155 60 C205 62 215 81 280 75 C340 68 390 37 440 15 C392 39 345 74 282 78 C218 84 201 65 153 64 C103 63 59 86 5 88 Z"
                  />
                  {/* máy bay giấy, hướng lên phải */}
                  <path
                    className="swoosh-plane"
                    d="M475 -4 L440 12 L453 16 L457 26 L463 20 L473 24 Z"
                  />
                  <path
                    className="swoosh-plane-fold"
                    d="M453 16 L475 -4"
                  />
                </svg>
              </div>
              <h2>{t("home.hero_desktop_title")}</h2>
              <p className="home__searching-hero-tagline">
                {t("home.hero_desktop_desc")}
              </p>
            </div>
            <div className="home__searching-content home__searching-content--search">
              <div className="home__searching--search-bridge">
                <Row className="home__searching--form-row">
                  <Col span={24} className="home__searching--form-overlap">
                    <div
                      className="home__searching--tabs-bar"
                      role="tablist"
                      aria-label={t("home.searching_placeholder")}
                    >
                      {tabs.map((item) => {
                        const isActive = tab === item.key;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={`home__searching--tab${isActive ? " home__searching--tab--active" : ""}`}
                            onClick={() => setTab(item.key)}
                          >
                            <span className="home__searching--tab-icon">
                              {renderTabIcon(item.key, isActive)}
                            </span>
                            <span className="home__searching--tab-label">
                              {item.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <Card
                      size="small"
                      variant="borderless"
                      className="home__searching--form-panel"
                    >
                      {tab === "flights" && <SearchingFlights layout="hero" />}
                      {tab === "hotels" && <SearchingHotels size="small" />}
                      {tab === "tours" && <FormSearchTours size="small" />}
                      {tab === "esim" && <FormSearchEsim size="small" />}
                      {tab === "visa" && (
                        <div className="home__searching--visa-form">
                          <div className="visa__hero-top">
                            <div className="visa__hero-copy">
                              <h1>{t("visa.hero.title")}</h1>
                              <p>{t("visa.hero.subtitle")}</p>
                            </div>
                            <VisaHeroHighlights variant="compact" />
                          </div>
                          <FormSearchVisa />
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>

                {activeTagline && tab !== "visa" && (
                  <div
                    className={`home__searching--tagline home__searching--tagline--${activeTagline.variant}`}
                  >
                    <div className="home__searching--tagline-lead">
                      <span className="home__searching--tagline-icon">
                        {activeTagline.icon}
                      </span>
                      <div className="home__searching--tagline-heading">
                        <span className="home__searching--tagline-title">
                          {t(activeTagline.titleKey)}
                        </span>
                        <span className="home__searching--tagline-desc">
                          {t(activeTagline.descKey)}
                        </span>
                      </div>
                    </div>

                    <div
                      className="home__searching--tagline-features"
                      role="list"
                    >
                      {activeTagline.features.map((feature) => (
                        <div
                          key={feature.key}
                          className="home__searching--tagline-feature"
                          role="listitem"
                        >
                          <span
                            className="home__searching--tagline-feature-icon"
                            aria-hidden
                          >
                            {feature.icon}
                          </span>
                          <div className="home__searching--tagline-feature-text">
                            <span className="home__searching--tagline-feature-title">
                              {t(feature.titleKey)}
                            </span>
                            <span className="home__searching--tagline-feature-desc">
                              {t(feature.descKey)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default SearchingVendorDesktop;
